import React, { useEffect, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Loader2,
} from 'lucide-react';
import { SkeletonDetail } from '../components/Skeleton';
import { useToast } from '../components/ToastProvider';
import {
  ContractBasicInformationSection,
  ContractConfidentialitySection,
  ContractContactSection,
  ContractCounterpartySection,
  ContractDatesTermsSection,
  ContractFinancialTermsSection,
  ContractNotesSection,
  ContractRelationshipsSection,
  ContractSummaryFlagsSection,
  ContractTagsSection,
} from '../features/contracts/ContractFormSections';
import useContractFormWorkflow from '../features/contracts/useContractFormWorkflow';
import SectionNav from '../components/SectionNav';

// Ordered by edit frequency: the facts legal corrects most often (dates,
// value) sit right under Basic info. Section ids are load-bearing — they are
// the #sec-* deep-link targets used by the detail page's fact strip and rail
// pencils, so reorder freely but never rename.
const FORM_SECTIONS = [
  { id: 'sec-basic', label: 'Basic info' },
  { id: 'sec-dates', label: 'Dates & terms' },
  { id: 'sec-financial', label: 'Financial' },
  { id: 'sec-counterparty', label: 'Counterparty' },
  { id: 'sec-contact', label: 'Contact' },
  { id: 'sec-parent', label: 'Parent contract' },
  { id: 'sec-summary', label: 'Summary & flags' },
  { id: 'sec-confidentiality', label: 'Confidentiality' },
  { id: 'sec-tags', label: 'Tags' },
  { id: 'sec-notes', label: 'Notes' },
];

export default function ContractForm() {
  const { id } = useParams();
  const { showToast } = useToast();
  const location = useLocation();
  const isEditing = Boolean(id);

  const {
    contractTypeOptions,
    counterpartySuggestions,
    cpFuzzySuggestions,
    error,
    errors,
    existingShortNamesForCp,
    formData,
    groupSuggestions,
    handleAddTag,
    handleBlur,
    handleChange,
    handleRemoveTag,
    handleSubmit,
    isDirty,
    loading,
    parentContract,
    saving,
    setCpFuzzyDismissed,
    setCpFuzzySuggestions,
    setFormData,
    setParentContract,
    tagSuggestions,
    touched,
  } = useContractFormWorkflow({ id, isEditing, showToast });

  // With the sticky save bar the user may be mid-page when a save fails, so
  // bring the top error callout back into view.
  const errorRef = useRef(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  // #sec-* deep links (fact strip Add links, rail pencils): scroll the target
  // section into view and flash it once so the eye lands on the right card.
  useEffect(() => {
    if (loading) return undefined;
    const hash = (location.hash || '').slice(1);
    if (!/^sec-/.test(hash)) return undefined;
    const el = document.getElementById(hash);
    if (!el) return undefined;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('section-flash');
    const timer = setTimeout(() => el.classList.remove('section-flash'), 1400);
    return () => {
      clearTimeout(timer);
      el.classList.remove('section-flash');
    };
  }, [loading, location.hash]);

  if (loading) {
    return (
      <div className="canvas-inner animate-fade-in">
        <SkeletonDetail />
      </div>
    );
  }

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div style={{ minWidth: 0 }}>
          <div className="page-eyebrow" style={{ marginBottom: 8 }}>
            <Link
              to={isEditing ? `/contracts/${id}` : '/contracts'}
              className="row"
              style={{ gap: 4, color: 'inherit', textDecoration: 'none' }}
            >
              <ArrowLeft className="w-3 h-3" />
              {isEditing ? 'Back to contract' : 'Back to contracts'}
            </Link>
          </div>
          <h1 className="page-title">
            {isEditing ? 'Edit contract' : 'New contract'}
          </h1>
          <div className="page-subtitle">
            {isEditing ? 'Update the contract details below' : 'Fill in the details to create a new contract.'}
          </div>
        </div>
      </div>

      {error && (
        <div ref={errorRef} className="callout red" style={{ marginBottom: 20 }}>
          <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-red-500)', flexShrink: 0, marginTop: 1 }} />
          <div className="callout-body">
            <strong>Error</strong>
            <div style={{ fontSize: 12, marginTop: 2 }}>{error}</div>
          </div>
        </div>
      )}

      <div className="form-layout">
        <SectionNav sections={FORM_SECTIONS} />
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
          <div id="sec-basic">
            <ContractBasicInformationSection
              contractTypeOptions={contractTypeOptions}
              errors={errors}
              existingShortNamesForCp={existingShortNamesForCp}
              formData={formData}
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFormData={setFormData}
              touched={touched}
            />
          </div>

          <div id="sec-dates">
            <ContractDatesTermsSection
              formData={formData}
              handleChange={handleChange}
              expirationHint="Leave empty for evergreen contracts"
            />
          </div>

          <div id="sec-financial">
            <ContractFinancialTermsSection
              formData={formData}
              handleChange={handleChange}
            />
          </div>

          <div id="sec-counterparty">
            <ContractCounterpartySection
              counterpartySuggestions={counterpartySuggestions}
              cpFuzzySuggestions={cpFuzzySuggestions}
              formData={formData}
              groupSuggestions={groupSuggestions}
              setCpFuzzyDismissed={setCpFuzzyDismissed}
              setCpFuzzySuggestions={setCpFuzzySuggestions}
              setFormData={setFormData}
            />
          </div>

          <div id="sec-contact">
            <ContractContactSection
              formData={formData}
              handleChange={handleChange}
            />
          </div>

          <div id="sec-parent">
            <ContractRelationshipsSection
              formData={formData}
              handleChange={handleChange}
              id={id}
              parentContract={parentContract}
              setFormData={setFormData}
              setParentContract={setParentContract}
            />
          </div>

          <div id="sec-summary">
            <ContractSummaryFlagsSection
              formData={formData}
              handleChange={handleChange}
              id={id}
            />
          </div>

          <div id="sec-confidentiality">
            <ContractConfidentialitySection
              formData={formData}
              handleChange={handleChange}
            />
          </div>

          <div id="sec-tags">
            <ContractTagsSection
              formData={formData}
              handleAddTag={handleAddTag}
              handleRemoveTag={handleRemoveTag}
              options={tagSuggestions}
            />
          </div>

          <div id="sec-notes">
            <ContractNotesSection
              formData={formData}
              handleChange={handleChange}
              placeholder="Add any additional notes or context about this contract..."
            />
          </div>

          {/* Sticky save bar — the form is ~4,400px tall, so Save rides the
              bottom of the scrollport (.canvas is the scroll container)
              instead of living only at the very end of the page. */}
          <div
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 5,
              background: 'var(--surface-base)',
              borderTop: 'var(--stroke-xs) solid var(--border-primary)',
              padding: '12px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Link to={isEditing ? `/contracts/${id}` : '/contracts'} className="btn outline sm">
              Cancel
            </Link>
            <div className="row" style={{ gap: 10 }}>
              {isDirty && (
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Unsaved changes</span>
              )}
              <button
                type="submit"
                disabled={saving || (isEditing && !isDirty)}
                className="btn primary sm"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    {isEditing ? 'Update contract' : 'Create contract'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
